var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Text } from '../../../atoms/Text/Text';
import { Button } from '../../../atoms/Button/Button';
import { ReplyEmailField } from './ReplyEmailField';
import { newClientRequestId } from '../../../../features/AIChatWidget/services/chatApi';
import { useOptionalSupportSession } from '../../../../features/AIChatWidget/contexts/SupportSessionContext';
import { displayableTicketNumber, isValidContactEmail } from '../../../../features/AIChatWidget/helpers/supportHelpers';
import { SUPPORT_CHANNEL_EMAIL, SUPPORT_CHANNEL_LIVE_CHAT, SUPPORT_COPY, SUPPORT_ERROR, SUPPORT_OFFER_STATE } from '../../../../features/AIChatWidget/constants/support';
import './SupportHandoffOfferRendererStyles.scss';
const baseClass = 'qmb-ui-SupportHandoffOfferRenderer';
/**
 * Session-scoped record of the offers already acted on.
 *
 * The streaming message remounts into its persisted copy on stream_end, and local state does
 * not survive that remount: without this the buttons would come back after a click and a second
 * click would start a second handoff. Keyed by the server's `offer_id`, like
 * ResolutionConfirmationRenderer's answered map.
 */
const completedOffers = new Map();
/** Test-only: clear completed offers between Jest cases. */
export function resetCompletedSupportOffersForTests() {
    completedOffers.clear();
}
/** The inline message for a refused reply address, by the server's stable error code. */
function emailErrorFor(code) {
    if (code === SUPPORT_ERROR.INVALID_CONTACT_EMAIL)
        return SUPPORT_COPY.OFFER_EMAIL_INVALID;
    if (code === SUPPORT_ERROR.CONTACT_EMAIL_REQUIRED)
        return SUPPORT_COPY.OFFER_EMAIL_REQUIRED;
    return null;
}
function OfferStatusCard({ outcome, fallbackEmail }) {
    var _a;
    const isEmail = outcome.channel === SUPPORT_CHANNEL_EMAIL;
    const statusText = isEmail
        ? SUPPORT_COPY.OFFER_EMAIL_SENT((_a = outcome.contactEmail) !== null && _a !== void 0 ? _a : fallbackEmail)
        : SUPPORT_COPY.OFFER_CHAT_STARTED;
    return (React.createElement(AIGeneralBlockCard, { sections: [
            {
                content: (React.createElement("div", { className: `${baseClass}__status`, role: "status" },
                    React.createElement("i", { className: isEmail ? 'fa-light fa-envelope-circle-check' : 'fa-light fa-comments', "aria-hidden": "true" }),
                    React.createElement(Text, { variant: "body" }, statusText)))
            }
        ] }));
}
/**
 * A replayed offer the server no longer honours (expired, already used, or unreadable): one muted
 * line, no buttons. It is never empty, so a message that holds only this card never reads as a
 * blank bubble under the agent badge.
 */
function OfferExpiredLine() {
    return (React.createElement("div", { className: `${baseClass}__expired`, "data-testid": "support-offer-expired" },
        React.createElement("i", { className: "fa-light fa-clock", "aria-hidden": "true" }),
        React.createElement(Text, { variant: "x-small" }, SUPPORT_COPY.OFFER_EXPIRED)));
}
/**
 * SupportHandoffOfferRenderer
 *
 * One card, one click. "Chat now" starts a live chat; "Email us instead" sends the conversation
 * to InspectPoint Support by email. The click IS the customer's confirmation, so the card says
 * up front that the conversation is shared with the support team.
 *
 * Copy rules, all deliberate:
 * - Live chat is **queued**: "the next available agent". Nothing promises a time.
 * - Outside support hours (`live_chat_available` or `hours_open` false) there is no chat button,
 *   the body says when the team is available, and the only action is "Email support".
 * - The hours line is shown ONLY while closed.
 * - `has_open_ticket`: a muted line says the message joins the open request. The offer is made
 *   either way and the buttons do not change.
 * - `email_requires_input` (no address on file): the email option asks "Where should we reply?",
 *   checks the shape client-side, and sends it as `contact_email`. A 422 from the server shows
 *   inline under the field; the typed address is never saved.
 *
 * After a click the card collapses to a single status line and the buttons are gone. A chat
 * hands over to the session banner above the composer; an email says where the reply will go
 * (the server confirms the address; a suppressed lower-environment ticket reads identically).
 * A failure keeps the card and says so, so the customer can retry.
 *
 * History (B46): the block is persisted with the assistant message and re-decided by the server on
 * every history load. `state: "offered"` replays the normal actionable card with CURRENT
 * availability. `state: "expired"` (expired, already used, or unreadable) replays as one muted
 * non-actionable line instead (this view's own click still wins: it shows what happened here).
 *
 * @version 4.0.0
 * @since 2026-10-27
 */
export function SupportHandoffOfferRenderer({ block }) {
    var _a, _b;
    const offer = block;
    const support = useOptionalSupportSession();
    const [outcome, setOutcome] = useState(() => { var _a; return (_a = completedOffers.get(offer.offer_id)) !== null && _a !== void 0 ? _a : null; });
    const [failed, setFailed] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(null);
    const inFlightRef = useRef(false);
    const mountedRef = useRef(true);
    const emailInputRef = useRef(null);
    // One request id per channel per card: a double click or a retry of a channel is the SAME
    // handoff, while the other channel is a different one. A changed or refused address is a
    // different email handoff, so editing the field drops the email id (see `handleEmailChange`).
    const requestIdsRef = useRef({});
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    const isOpen = offer.hours_open && offer.live_chat_available;
    const canChat = isOpen && support !== null;
    const canEmail = offer.email_available && support !== null;
    const needsEmail = offer.email_requires_input === true;
    const handleEmailChange = useCallback((value) => {
        setEmail(value);
        setEmailError(null);
        delete requestIdsRef.current[SUPPORT_CHANNEL_EMAIL];
    }, []);
    const handleChoose = useCallback((channel) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!support || inFlightRef.current)
            return;
        const asksForEmail = channel === SUPPORT_CHANNEL_EMAIL && needsEmail;
        const contactEmail = asksForEmail ? email.trim() : undefined;
        if (asksForEmail && !isValidContactEmail(email)) {
            setEmailError(contactEmail ? SUPPORT_COPY.OFFER_EMAIL_INVALID : SUPPORT_COPY.OFFER_EMAIL_REQUIRED);
            (_a = emailInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        inFlightRef.current = true;
        setFailed(false);
        setEmailError(null);
        if (!requestIdsRef.current[channel])
            requestIdsRef.current[channel] = newClientRequestId();
        try {
            const result = yield support.startHandoff(Object.assign({ channel, offerId: offer.offer_id, clientRequestId: requestIdsRef.current[channel] }, (contactEmail ? { contactEmail } : {})));
            if (!result.ok) {
                if (!mountedRef.current)
                    return;
                const fieldError = asksForEmail ? emailErrorFor(result.errorCode) : null;
                if (fieldError) {
                    delete requestIdsRef.current[channel]; // refused before anything was sent: the fix is a new request
                    setEmailError(fieldError);
                    (_b = emailInputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                }
                else {
                    setFailed(true);
                }
                return;
            }
            const done = {
                channel,
                contactEmail: result.channel === SUPPORT_CHANNEL_EMAIL ? result.contactEmail : null
            };
            completedOffers.set(offer.offer_id, done);
            if (mountedRef.current)
                setOutcome(done);
        }
        finally {
            inFlightRef.current = false;
        }
    }), [support, offer.offer_id, needsEmail, email]);
    // A chat opened from anywhere (another tab, a refetched snapshot) already answers a LIVE offer.
    // An expired one is a card from the past: with history replaying every offer, a chat that is open
    // now must not make each old card claim "Chat started", so only this view's own click counts.
    const isExpired = offer.state === SUPPORT_OFFER_STATE.EXPIRED;
    const chatAlreadyOpen = !isExpired && ((_a = support === null || support === void 0 ? void 0 : support.isActive) !== null && _a !== void 0 ? _a : false);
    const done = outcome !== null && outcome !== void 0 ? outcome : (chatAlreadyOpen ? { channel: SUPPORT_CHANNEL_LIVE_CHAT, contactEmail: null } : null);
    if (done)
        return React.createElement(OfferStatusCard, { outcome: done, fallbackEmail: offer.contact_email });
    if (isExpired)
        return React.createElement(OfferExpiredLine, null);
    if (!canChat && !canEmail)
        return null;
    // The hours line belongs to "closed" only: even if a note leaks while hours are open, it is not shown.
    const hoursNote = offer.hours_open ? undefined : offer.hours_note;
    const bodyText = isOpen ? SUPPORT_COPY.OFFER_BODY : SUPPORT_COPY.OFFER_CLOSED_BODY(hoursNote, offer.contact_email);
    const disabled = (_b = support === null || support === void 0 ? void 0 : support.isStarting) !== null && _b !== void 0 ? _b : false;
    const sections = [
        {
            content: (React.createElement("div", { className: baseClass },
                React.createElement("div", { className: `${baseClass}__heading` },
                    React.createElement("i", { className: "fa-light fa-headset", "aria-hidden": "true" }),
                    React.createElement(Text, { variant: "small", as: "h3", className: `${baseClass}__title` }, SUPPORT_COPY.OFFER_TITLE)),
                React.createElement(Text, { variant: "body", className: `${baseClass}__body` }, bodyText),
                offer.has_open_ticket && (React.createElement(Text, { variant: "body", className: `${baseClass}__note` }, SUPPORT_COPY.OFFER_OPEN_TICKET_NOTE(displayableTicketNumber(offer.open_ticket_number)))),
                React.createElement("div", { className: `${baseClass}__actions` },
                    canChat && (React.createElement("div", { className: `${baseClass}__action` },
                        React.createElement(Button, { variant: "primary", disabled: disabled, onClick: () => {
                                void handleChoose(SUPPORT_CHANNEL_LIVE_CHAT);
                            } },
                            React.createElement("i", { className: "fa-light fa-comments", "aria-hidden": "true" }),
                            SUPPORT_COPY.OFFER_CHAT_LABEL),
                        React.createElement(Text, { variant: "body", className: `${baseClass}__note` }, SUPPORT_COPY.OFFER_CHAT_NOTE))),
                    canEmail && (React.createElement("div", { className: `${baseClass}__action${needsEmail ? ` ${baseClass}__action--field` : ''}` },
                        needsEmail && (React.createElement(ReplyEmailField, { ref: emailInputRef, id: `${baseClass}-email-${offer.offer_id}`, value: email, error: emailError, disabled: disabled, onChange: handleEmailChange, onSubmit: () => {
                                void handleChoose(SUPPORT_CHANNEL_EMAIL);
                            } })),
                        React.createElement(Button, { variant: canChat ? 'secondary' : 'primary', disabled: disabled, onClick: () => {
                                void handleChoose(SUPPORT_CHANNEL_EMAIL);
                            } },
                            React.createElement("i", { className: "fa-light fa-envelope", "aria-hidden": "true" }),
                            canChat ? SUPPORT_COPY.OFFER_EMAIL_LABEL : SUPPORT_COPY.OFFER_EMAIL_ONLY_LABEL),
                        canChat && !needsEmail && (React.createElement(Text, { variant: "body", className: `${baseClass}__note` }, SUPPORT_COPY.OFFER_EMAIL_NOTE(offer.contact_email)))))),
                failed && (React.createElement(Text, { variant: "x-small", className: `${baseClass}__error`, role: "alert" }, SUPPORT_COPY.OFFER_RETRY_ERROR))))
        }
    ];
    return React.createElement(AIGeneralBlockCard, { sections: sections });
}
